import React, { useCallback, useState } from 'react';
import {
  Files,
  Search,
  AlertCircle,
  Bug,
  ChevronRight,
  ChevronDown,
  FileCode,
  FileText,
  FolderOpen,
  Folder,
  X,
  CaseSensitive,
  WholeWord,
  Regex,
} from 'lucide-react';
import { useScriptEditorStore } from '../../store/scriptEditorStore';
import { SidebarTab, DiagnosticMessage, SearchResult, FileSystemNode } from '../../../core/types/script-editor.types';
import { cn } from '../../utils/cn';
import { FileTreeContextMenu } from './FileTreeContextMenu';
import { InlineRenameInput } from './InlineRenameInput';

interface SidebarTabButtonProps {
  tab: SidebarTab;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
  badge?: number;
}

const SidebarTabButton: React.FC<SidebarTabButtonProps> = ({
  icon,
  label,
  isActive,
  onClick,
  badge,
}) => (
  <button
    onClick={onClick}
    className={cn(
      'flex items-center justify-center w-10 h-10 transition-colors relative',
      isActive ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50'
    )}
    title={label}
  >
    {icon}
    {badge !== undefined && badge > 0 && (
      <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 text-[10px] font-medium bg-red-500 text-white rounded-full flex items-center justify-center">
        {badge > 99 ? '99+' : badge}
      </span>
    )}
  </button>
);

// File tree node component
interface FileTreeNodeProps {
  node: FileSystemNode;
  depth: number;
  expandedFolders: Set<string>;
  toggleFolder: (folderId: string) => void;
  activeTabId: string | null;
  openFiles: Record<string, { id: string; isDirty: boolean }>;
  renamingNodeId: string | null;
  dragOverNodeId: string | null;
  onFileClick: (path: string) => void;
  onContextMenu: (e: React.MouseEvent, nodeId: string, nodeType: 'file' | 'folder') => void;
  onRenameSubmit: (nodeId: string, newName: string) => void;
  onRenameCancel: () => void;
  onDragStart: (e: React.DragEvent, nodeId: string) => void;
  onDragOver: (e: React.DragEvent, nodeId: string) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetNodeId: string) => void;
  getNodeChildren: (nodeId: string) => FileSystemNode[];
}

const FileTreeNode: React.FC<FileTreeNodeProps> = ({
  node,
  depth,
  expandedFolders,
  toggleFolder,
  activeTabId,
  openFiles,
  renamingNodeId,
  dragOverNodeId,
  onFileClick,
  onContextMenu,
  onRenameSubmit,
  onRenameCancel,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  getNodeChildren,
}) => {
  const isFolder = node.type === 'folder';
  const isExpanded = expandedFolders.has(node.id);
  const isRenaming = renamingNodeId === node.id;
  const isDragOver = dragOverNodeId === node.id && isFolder;
  const paddingLeft = 8 + depth * 16;

  // Check if this file is open and if it's the active one
  const openFile = openFiles[node.id];
  const isActive = activeTabId === node.id;

  const handleClick = () => {
    if (isFolder) {
      toggleFolder(node.id);
    } else {
      onFileClick(node.path);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onContextMenu(e, node.id, node.type);
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.stopPropagation();
    onDragStart(e, node.id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isFolder) {
      onDragOver(e, node.id);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.stopPropagation();
    onDragLeave(e);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isFolder) {
      onDrop(e, node.id);
    }
  };

  // Render file icon based on language
  const renderIcon = () => {
    if (isFolder) {
      return isExpanded ? (
        <FolderOpen className="w-4 h-4 text-yellow-500 flex-shrink-0" />
      ) : (
        <Folder className="w-4 h-4 text-yellow-500 flex-shrink-0" />
      );
    }

    if (node.language === 'typescript') {
      return <FileCode className="w-4 h-4 text-blue-400 flex-shrink-0" />;
    }
    return <FileText className="w-4 h-4 text-yellow-400 flex-shrink-0" />;
  };

  return (
    <>
      <div
        className={cn(
          'flex items-center gap-1 py-1 pr-2 cursor-pointer select-none',
          'hover:bg-zinc-800/50',
          isActive && !isFolder && 'bg-zinc-800 text-zinc-100',
          isDragOver && 'bg-blue-600/30 outline outline-1 outline-blue-500'
        )}
        style={{ paddingLeft }}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        draggable={!isRenaming}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isFolder && (
          isExpanded ? (
            <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />
          )
        )}
        {!isFolder && <span className="w-4 flex-shrink-0" />}
        {renderIcon()}
        {isRenaming ? (
          <InlineRenameInput
            initialValue={node.name}
            onSubmit={(newName) => onRenameSubmit(node.id, newName)}
            onCancel={onRenameCancel}
            isFolder={isFolder}
          />
        ) : (
          <span className={cn(
            'truncate text-sm',
            isActive && !isFolder ? 'text-zinc-100' : 'text-zinc-400'
          )}>
            {node.name}
          </span>
        )}
        {openFile?.isDirty && (
          <span className="w-2 h-2 rounded-full bg-blue-400 ml-auto flex-shrink-0" />
        )}
      </div>
      {isFolder && isExpanded && (
        <>
          {getNodeChildren(node.id).map(childNode => (
            <FileTreeNode
              key={childNode.id}
              node={childNode}
              depth={depth + 1}
              expandedFolders={expandedFolders}
              toggleFolder={toggleFolder}
              activeTabId={activeTabId}
              openFiles={openFiles}
              renamingNodeId={renamingNodeId}
              dragOverNodeId={dragOverNodeId}
              onFileClick={onFileClick}
              onContextMenu={onContextMenu}
              onRenameSubmit={onRenameSubmit}
              onRenameCancel={onRenameCancel}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              getNodeChildren={getNodeChildren}
            />
          ))}
        </>
      )}
    </>
  );
};

// Explorer Tab Content
const ExplorerContent: React.FC = () => {
  const {
    files,
    openTabs,
    activeTabId,
    setActiveTab,
    openFile,
    fileTree,
    rootFolderId,
    createFolder,
    renameNode,
    deleteNode,
    moveNode,
    getNodeChildren,
    createFile,
  } = useScriptEditorStore();

  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['open-editors', 'root-scripts']));
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    nodeId: string | null;
    nodeType: 'file' | 'folder' | null;
  } | null>(null);
  const [renamingNodeId, setRenamingNodeId] = useState<string | null>(null);
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const [dragOverNodeId, setDragOverNodeId] = useState<string | null>(null);

  const toggleFolder = (folder: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folder)) {
        next.delete(folder);
      } else {
        next.add(folder);
      }
      return next;
    });
  };

  // Group files by folder (mock structure)
  const openFiles = openTabs.map(tab => files[tab.fileId]).filter(Boolean);

  // Create a lookup map of open files by their IDs
  const openFilesMap = openFiles.reduce((acc, file) => {
    acc[file.id] = { id: file.id, isDirty: file.isDirty };
    return acc;
  }, {} as Record<string, { id: string; isDirty: boolean }>);

  // Context menu handlers
  const handleCreateFolder = async (parentId: string | null) => {
    const targetParentId = parentId || rootFolderId || 'root-scripts';
    const parentNode = fileTree[targetParentId];
    const parentPath = parentNode?.path || 'assets/scripts';

    const newFolderId = await createFolder(parentPath, 'New Folder');

    // Expand the parent folder to show the new folder
    if (targetParentId) {
      setExpandedFolders(prev => new Set([...prev, targetParentId]));
    }

    setRenamingNodeId(newFolderId);
    setContextMenu(null);
  };

  const handleCreateScript = async (parentId: string | null, language: 'typescript' | 'javascript') => {
    const ext = language === 'typescript' ? '.ts' : '.js';
    const targetParentId = parentId || rootFolderId || 'root-scripts';
    const parentNode = fileTree[targetParentId];
    const parentPath = parentNode?.path || 'assets/scripts';
    const path = `${parentPath}/NewScript${ext}`;

    const fileId = await createFile(path, language);

    // Expand the parent folder to show the new file
    if (targetParentId) {
      setExpandedFolders(prev => new Set([...prev, targetParentId]));
    }

    setRenamingNodeId(fileId);
    setContextMenu(null);
  };

  const handleRename = (nodeId: string) => {
    setRenamingNodeId(nodeId);
    setContextMenu(null);
  };

  const handleDelete = async (nodeId: string) => {
    await deleteNode(nodeId);
    setContextMenu(null);
  };

  const handleRenameSubmit = async (nodeId: string, newName: string) => {
    await renameNode(nodeId, newName);
    setRenamingNodeId(null);
  };

  const handleRenameCancel = () => {
    setRenamingNodeId(null);
  };

  const handleNodeContextMenu = (e: React.MouseEvent, nodeId: string, nodeType: 'file' | 'folder') => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      nodeId,
      nodeType,
    });
  };

  const handleContainerContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      nodeId: null,
      nodeType: null,
    });
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, nodeId: string) => {
    setDraggingNodeId(nodeId);
    e.dataTransfer.setData('text/plain', nodeId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, targetNodeId: string) => {
    e.preventDefault();
    // Don't allow dropping on self or on a child of the dragged node
    if (draggingNodeId === targetNodeId) return;

    // Check if target is a descendant of the dragging node (prevent circular move)
    const isDescendant = (parentId: string, childId: string): boolean => {
      const parent = fileTree[parentId];
      if (!parent || parent.type !== 'folder' || !parent.children) return false;
      if (parent.children.includes(childId)) return true;
      return parent.children.some(id => isDescendant(id, childId));
    };

    if (draggingNodeId && isDescendant(draggingNodeId, targetNodeId)) return;

    setDragOverNodeId(targetNodeId);
  };

  const handleDragLeave = () => {
    setDragOverNodeId(null);
  };

  const handleDrop = async (e: React.DragEvent, targetNodeId: string) => {
    e.preventDefault();
    const sourceNodeId = e.dataTransfer.getData('text/plain');

    if (sourceNodeId && sourceNodeId !== targetNodeId) {
      const sourceNode = fileTree[sourceNodeId];
      const targetNode = fileTree[targetNodeId];

      // Only allow dropping into folders
      if (targetNode?.type === 'folder') {
        // Don't allow dropping a folder into itself or its children
        const isDescendant = (parentId: string, childId: string): boolean => {
          const parent = fileTree[parentId];
          if (!parent || parent.type !== 'folder' || !parent.children) return false;
          if (parent.children.includes(childId)) return true;
          return parent.children.some(id => isDescendant(id, childId));
        };

        if (!isDescendant(sourceNodeId, targetNodeId)) {
          await moveNode(sourceNodeId, targetNodeId);
          // Expand the target folder to show the moved item
          setExpandedFolders(prev => new Set([...prev, targetNodeId]));
        }
      }
    }

    setDraggingNodeId(null);
    setDragOverNodeId(null);
  };

  const handleDragEnd = () => {
    setDraggingNodeId(null);
    setDragOverNodeId(null);
  };

  const rootNode = rootFolderId ? fileTree[rootFolderId] : null;

  return (
    <div className="flex flex-col h-full">
      {/* Open Editors Section */}
      <div className="border-b border-zinc-800">
        <button
          onClick={() => toggleFolder('open-editors')}
          className="flex items-center gap-1 w-full px-2 py-1.5 text-xs font-medium text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50"
        >
          {expandedFolders.has('open-editors') ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
          OPEN EDITORS
          <span className="ml-auto text-zinc-600">{openFiles.length}</span>
        </button>
        {expandedFolders.has('open-editors') && (
          <div className="pb-1">
            {openFiles.map(file => (
              <button
                key={file.id}
                onClick={() => setActiveTab(file.id)}
                className={cn(
                  'flex items-center gap-2 w-full px-4 py-1 text-sm hover:bg-zinc-800/50',
                  activeTabId === file.id ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-400'
                )}
              >
                {file.language === 'typescript' ? (
                  <FileCode className="w-4 h-4 text-blue-400 flex-shrink-0" />
                ) : (
                  <FileText className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                )}
                <span className="truncate">{file.name}</span>
                {file.isDirty && (
                  <span className="w-2 h-2 rounded-full bg-blue-400 ml-auto flex-shrink-0" />
                )}
              </button>
            ))}
            {openFiles.length === 0 && (
              <div className="px-4 py-2 text-xs text-zinc-600">No open files</div>
            )}
          </div>
        )}
      </div>

      {/* Scripts Folder Section */}
      <div
        className="flex-1 overflow-y-auto"
        onContextMenu={handleContainerContextMenu}
        onDragEnd={handleDragEnd}
      >
        {rootNode && (
          <FileTreeNode
            node={rootNode}
            depth={0}
            expandedFolders={expandedFolders}
            toggleFolder={toggleFolder}
            activeTabId={activeTabId}
            openFiles={openFilesMap}
            renamingNodeId={renamingNodeId}
            dragOverNodeId={dragOverNodeId}
            onFileClick={openFile}
            onContextMenu={handleNodeContextMenu}
            onRenameSubmit={handleRenameSubmit}
            onRenameCancel={handleRenameCancel}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            getNodeChildren={getNodeChildren}
          />
        )}
        {!rootNode && (
          <div className="px-4 py-2 text-xs text-zinc-600">No scripts folder</div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <FileTreeContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          nodeId={contextMenu.nodeId}
          nodeType={contextMenu.nodeType}
          onClose={() => setContextMenu(null)}
          onCreateFolder={handleCreateFolder}
          onCreateScript={handleCreateScript}
          onRename={handleRename}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

// Search Tab Content
const SearchContent: React.FC = () => {
  const {
    searchQuery,
    searchResults,
    searchOptions,
    isSearching,
    search,
    setSearchOptions,
    clearSearch,
    goToSearchResult,
  } = useScriptEditorStore();

  const [localQuery, setLocalQuery] = useState(searchQuery);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    search(localQuery);
  }, [localQuery, search]);

  const handleClear = useCallback(() => {
    setLocalQuery('');
    clearSearch();
  }, [clearSearch]);

  return (
    <div className="flex flex-col h-full">
      {/* Search Input */}
      <form onSubmit={handleSearch} className="p-2 border-b border-zinc-800">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            value={localQuery}
            onChange={(e) => setLocalQuery(e.target.value)}
            placeholder="Search in files..."
            className="w-full pl-8 pr-8 py-1.5 text-sm bg-zinc-800 border border-zinc-700 rounded text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-blue-500"
          />
          {localQuery && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Search Options */}
        <div className="flex items-center gap-1 mt-2">
          <button
            type="button"
            onClick={() => setSearchOptions({ caseSensitive: !searchOptions.caseSensitive })}
            className={cn(
              'p-1 rounded transition-colors',
              searchOptions.caseSensitive
                ? 'bg-blue-600 text-white'
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700'
            )}
            title="Match Case"
          >
            <CaseSensitive className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setSearchOptions({ wholeWord: !searchOptions.wholeWord })}
            className={cn(
              'p-1 rounded transition-colors',
              searchOptions.wholeWord
                ? 'bg-blue-600 text-white'
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700'
            )}
            title="Match Whole Word"
          >
            <WholeWord className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setSearchOptions({ useRegex: !searchOptions.useRegex })}
            className={cn(
              'p-1 rounded transition-colors',
              searchOptions.useRegex
                ? 'bg-blue-600 text-white'
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700'
            )}
            title="Use Regular Expression"
          >
            <Regex className="w-4 h-4" />
          </button>
        </div>
      </form>

      {/* Search Results */}
      <div className="flex-1 overflow-y-auto">
        {isSearching ? (
          <div className="flex items-center justify-center py-8 text-zinc-500">
            <div className="w-5 h-5 border-2 border-zinc-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : searchResults.length > 0 ? (
          <div className="py-1">
            <div className="px-3 py-1 text-xs text-zinc-500">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} in{' '}
              {new Set(searchResults.map(r => r.fileId)).size} file{searchResults.length !== 1 ? 's' : ''}
            </div>
            {searchResults.map((result, index) => (
              <SearchResultItem
                key={`${result.fileId}-${result.line}-${result.column}-${index}`}
                result={result}
                onClick={() => goToSearchResult(result)}
              />
            ))}
          </div>
        ) : searchQuery ? (
          <div className="px-3 py-4 text-sm text-zinc-500 text-center">
            No results found
          </div>
        ) : (
          <div className="px-3 py-4 text-sm text-zinc-500 text-center">
            Enter a search term to find in files
          </div>
        )}
      </div>
    </div>
  );
};

const SearchResultItem: React.FC<{ result: SearchResult; onClick: () => void }> = ({ result, onClick }) => {
  const fileName = result.filePath.split('/').pop() || result.filePath;

  return (
    <button
      onClick={onClick}
      className="flex flex-col w-full px-3 py-1 text-left hover:bg-zinc-800/50"
    >
      <div className="flex items-center gap-1 text-xs text-zinc-400">
        <FileCode className="w-3 h-3" />
        <span>{fileName}</span>
        <span className="text-zinc-600">:{result.line}</span>
      </div>
      <div className="text-sm text-zinc-300 truncate font-mono">
        {result.lineContent.trim()}
      </div>
    </button>
  );
};

// Problems Tab Content
const ProblemsContent: React.FC = () => {
  const { diagnostics, setActiveTab, files } = useScriptEditorStore();

  const handleDiagnosticClick = (diagnostic: DiagnosticMessage) => {
    setActiveTab(diagnostic.fileId);
    // TODO: Navigate to line/column in editor
  };

  const getSeverityIcon = (severity: DiagnosticMessage['severity']) => {
    switch (severity) {
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-400" />;
      case 'info':
        return <AlertCircle className="w-4 h-4 text-blue-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-zinc-400" />;
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {diagnostics.length > 0 ? (
        <div className="py-1">
          {diagnostics.map((diagnostic) => {
            const file = files[diagnostic.fileId];
            const fileName = file?.name || diagnostic.filePath.split('/').pop() || 'Unknown';

            return (
              <button
                key={diagnostic.id}
                onClick={() => handleDiagnosticClick(diagnostic)}
                className="flex items-start gap-2 w-full px-3 py-2 text-left hover:bg-zinc-800/50"
              >
                {getSeverityIcon(diagnostic.severity)}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-300 truncate">
                    {diagnostic.message}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <span>{fileName}</span>
                    <span>({diagnostic.line}:{diagnostic.column})</span>
                    {diagnostic.source && (
                      <span className="text-zinc-600">[{diagnostic.source}]</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 text-zinc-500">
          <AlertCircle className="w-8 h-8 mb-2 text-zinc-600" />
          <span className="text-sm">No problems detected</span>
        </div>
      )}
    </div>
  );
};

// Debug Tab Content
const DebugContent: React.FC = () => {
  return (
    <div className="flex flex-col items-center justify-center h-full text-zinc-500">
      <Bug className="w-8 h-8 mb-2 text-zinc-600" />
      <span className="text-sm">Debug panel</span>
      <span className="text-xs text-zinc-600 mt-1">Coming soon</span>
    </div>
  );
};

export const ScriptEditorSidebar: React.FC = () => {
  const { activeSidebarTab, setActiveSidebarTab, diagnosticCounts } = useScriptEditorStore();

  return (
    <div className="flex h-full bg-zinc-900 border-r border-zinc-800">
      {/* Tab Buttons */}
      <div className="flex flex-col border-r border-zinc-800">
        <SidebarTabButton
          tab="explorer"
          icon={<Files className="w-5 h-5" />}
          label="Explorer"
          isActive={activeSidebarTab === 'explorer'}
          onClick={() => setActiveSidebarTab('explorer')}
        />
        <SidebarTabButton
          tab="search"
          icon={<Search className="w-5 h-5" />}
          label="Search"
          isActive={activeSidebarTab === 'search'}
          onClick={() => setActiveSidebarTab('search')}
        />
        <SidebarTabButton
          tab="problems"
          icon={<AlertCircle className="w-5 h-5" />}
          label="Problems"
          isActive={activeSidebarTab === 'problems'}
          onClick={() => setActiveSidebarTab('problems')}
          badge={diagnosticCounts.errors + diagnosticCounts.warnings}
        />
        <SidebarTabButton
          tab="debug"
          icon={<Bug className="w-5 h-5" />}
          label="Debug"
          isActive={activeSidebarTab === 'debug'}
          onClick={() => setActiveSidebarTab('debug')}
        />
      </div>

      {/* Tab Content */}
      <div className="flex-1 min-w-0 overflow-hidden">
        {activeSidebarTab === 'explorer' && <ExplorerContent />}
        {activeSidebarTab === 'search' && <SearchContent />}
        {activeSidebarTab === 'problems' && <ProblemsContent />}
        {activeSidebarTab === 'debug' && <DebugContent />}
      </div>
    </div>
  );
};
